import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Avatar, Box, Skeleton, Text, useTheme } from '@ublend-npm/aulaui-core';
import { currentUser } from '@core/selectors/user';
import { getHomeUserFirstName } from '@app/selectors/home';
import GreetingTimeIcon, {
  getGreetingTimeOfDay,
  GREETING_TIME_LABELS,
  GreetingTimeOfDay,
} from './GreetingTimeIcon';
import { HOME_WIDGET_FONT_FAMILY } from '../homeWidgetTypography';
import {
  GREETING_CARD_HEIGHT_PX,
  GREETING_CARD_WIDTH_PX,
} from '../spaceWidget/spaceWidgetConstants';

const GREETING_SUBTITLE_COLOR = 'rgba(255, 255, 255, 0.7)';
const AVATAR_BG = '#D9D9D9';
const AVATAR_TEXT_COLOR = '#1A1A1A';
const SKELETON_SURFACE = 'rgba(255, 255, 255, 0.22)';

/** Figma Greetings Widget — padding & sizing */
const LG_PADDING_Y = '24px';
const LG_PADDING_X = '16px';
const LG_MIN_WIDTH = `${GREETING_CARD_WIDTH_PX}px`;
const LG_MIN_HEIGHT = `${GREETING_CARD_HEIGHT_PX}px`;
const LG_AVATAR_TOP = '111px';
const LG_GREETING_GAP = '15px';

const cardShellSx = {
  position: 'relative' as const,
  borderRadius: '24px',
  boxSizing: 'border-box' as const,
  width: {
    xs: '100%',
    sm: '100%',
    md: '100%',
    lg: '100%',
  },
  minWidth: {
    lg: LG_MIN_WIDTH,
  },
  height: {
    xs: '88px',
    sm: '88px',
    md: '70px',
    lg: '100%',
  },
  minHeight: {
    lg: LG_MIN_HEIGHT,
  },
  flexShrink: 0,
  px: {
    xs: 2,
    sm: 2,
    md: 3,
    lg: LG_PADDING_X,
  },
  py: {
    xs: 1.5,
    sm: 1.5,
    md: 1.5,
    lg: LG_PADDING_Y,
  },
};

const mobileIconSx = {
  position: 'absolute' as const,
  top: { xs: 12, sm: 12, md: 10 },
  right: { xs: 16, sm: 16, md: 24 },
  width: { xs: 40, sm: 40, md: 48 },
  height: { xs: 40, sm: 40, md: 48 },
  '& svg': { width: '100%', height: '100%' },
};

const lgIconSx = {
  position: 'absolute' as const,
  top: '24px',
  right: '16px',
  width: '75px',
  maxHeight: '72px',
  '& svg': { width: '100%', height: '100%' },
};

const lgGreetingTextSx = {
  fontFamily: HOME_WIDGET_FONT_FAMILY,
  fontSize: '1.5rem',
  fontWeight: 500,
  lineHeight: '133%',
  letterSpacing: 0,
};

const lgDateTextSx = {
  fontFamily: HOME_WIDGET_FONT_FAMILY,
  fontSize: '1rem',
  fontWeight: 400,
  lineHeight: '150%',
  letterSpacing: '0.15px',
  color: GREETING_SUBTITLE_COLOR,
};

const getAvatarAltText = (
  user?: { firstName?: string; lastName?: string } | null
) => {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  return name ? `Profile picture of ${name}` : 'Profile picture';
};

const greetingFocusSx = {
  outline: 'none',
  '&:focus-visible': {
    outline: '2px solid #FFFFFF',
    outlineOffset: '2px',
  },
};

/** Compact (MD-style) shell — short horizontal bar; used when greeting sits above the widget. */
const compactCardShellSx = {
  ...cardShellSx,
  width: '100%',
  minWidth: undefined,
  height: { xs: '88px', sm: '88px', md: '70px', lg: '70px' },
  minHeight: undefined,
  px: { xs: 2, sm: 2, md: 3, lg: 3 },
  py: { xs: 1.5, sm: 1.5, md: 1.5, lg: 1.5 },
};

type HomeGreetingSkeletonProps = {
  backgroundColor: string;
  isLg?: boolean;
  compact?: boolean;
};

const HomeGreetingSkeleton = ({
  backgroundColor,
  isLg = false,
  compact = false,
}: HomeGreetingSkeletonProps) => (
  <Box
    component="section"
    tabIndex={0}
    role="status"
    aria-live="polite"
    aria-busy="true"
    aria-label="Loading greeting"
    sx={{
      ...(compact ? compactCardShellSx : cardShellSx),
      ...greetingFocusSx,
      backgroundColor,
      overflow: 'hidden',
      display: isLg ? 'flex' : 'block',
      flexDirection: isLg ? 'column' : undefined,
    }}
  >
    <Skeleton
      kind="circular"
      sx={{
        ...(isLg ? lgIconSx : mobileIconSx),
        borderRadius: '50%',
        bgcolor: SKELETON_SURFACE,
      }}
    />

    {isLg ? (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <Skeleton
          kind="circular"
          width={40}
          height={40}
          sx={{
            mt: LG_AVATAR_TOP,
            bgcolor: SKELETON_SURFACE,
            flexShrink: 0,
          }}
        />
        <Skeleton
          kind="rounded"
          height={32}
          sx={{
            mt: LG_GREETING_GAP,
            width: '100%',
            borderRadius: '6px',
            bgcolor: SKELETON_SURFACE,
            flexShrink: 0,
          }}
        />
        <Skeleton
          kind="rounded"
          height={32}
          sx={{
            width: '72%',
            borderRadius: '6px',
            bgcolor: SKELETON_SURFACE,
            flexShrink: 0,
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Skeleton
          kind="rounded"
          height={21}
          sx={{
            width: '85%',
            borderRadius: '6px',
            bgcolor: SKELETON_SURFACE,
            flexShrink: 0,
          }}
        />
      </Box>
    ) : (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          gap: 2,
          pr: '56px',
          overflow: 'hidden',
        }}
      >
        <Skeleton
          kind="circular"
          width={40}
          height={40}
          sx={{ bgcolor: SKELETON_SURFACE, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton
            kind="rounded"
            height={22}
            sx={{
              bgcolor: SKELETON_SURFACE,
              width: '92%',
              borderRadius: '6px',
            }}
          />
          <Skeleton
            kind="rounded"
            height={18}
            sx={{
              bgcolor: SKELETON_SURFACE,
              width: '68%',
              borderRadius: '6px',
              mt: '6px',
            }}
          />
        </Box>
      </Box>
    )}
  </Box>
);

type HomeGreetingProps = {
  /** Forces the MD-style short horizontal greeting regardless of breakpoint. */
  compact?: boolean;
};

const HomeGreeting = ({ compact = false }: HomeGreetingProps) => {
  const theme = useTheme();
  const { palette } = theme;
  const user = useSelector(currentUser);
  const firstName = useSelector(getHomeUserFirstName);
  const [timeOfDay, setTimeOfDay] = useState<GreetingTimeOfDay | null>(null);
  const [isLgViewport, setIsLgViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(`(min-width: ${theme.breakpoints.values.lg}px)`)
      .matches;
  });
  const isLg = compact ? false : isLgViewport;

  const mobileGreetingTextSx = {
    fontFamily: HOME_WIDGET_FONT_FAMILY,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: compact
      ? { xs: '1.125rem', sm: '1.125rem', md: '1.25rem', lg: '1.25rem' }
      : { xs: '1.125rem', sm: '1.125rem', md: '1.25rem', lg: '1.5rem' },
    lineHeight: '133%',
    letterSpacing: compact ? { sm: 0 } : { sm: 0, lg: -1 },
    color: palette.common.white,
    marginTop: compact ? undefined : { lg: '15px' },
  };

  useEffect(() => {
    const mq = window.matchMedia(
      `(min-width: ${theme.breakpoints.values.lg}px)`
    );
    const update = () => setIsLgViewport(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, [theme.breakpoints.values.lg]);

  useEffect(() => {
    setTimeOfDay(getGreetingTimeOfDay());
  }, []);

  const todayLabel = moment().format('[Today is] Do MMMM YYYY');
  const initials = [user?.firstName?.[0], user?.lastName?.[0]]
    .filter(Boolean)
    .join('')
    .toUpperCase();
  const avatarAltText = getAvatarAltText(user);

  if (!timeOfDay) {
    return (
      <HomeGreetingSkeleton
        backgroundColor={'#452188'}
        isLg={isLg}
        compact={compact}
      />
    );
  }

  const greetingLabel = GREETING_TIME_LABELS[timeOfDay];

  return (
    <Box
      component="section"
      tabIndex={0}
      aria-labelledby="home-greeting-heading"
      sx={{
        ...(compact ? compactCardShellSx : cardShellSx),
        ...greetingFocusSx,
        backgroundColor: '#452188',
        color: palette.common.white,
        overflow: compact
          ? 'hidden'
          : { xs: 'hidden', sm: 'hidden', md: 'hidden', lg: 'visible' },
        display: compact ? 'block' : { xs: 'block', lg: 'flex' },
        flexDirection: compact ? undefined : { lg: 'column' },
      }}
    >
      <GreetingTimeIcon
        timeOfDay={timeOfDay}
        sx={{
          position: 'absolute',
          top: compact
            ? { xs: 12, sm: 12, md: 10, lg: 10 }
            : { xs: 12, sm: 12, md: 10, lg: 24 },
          right: compact
            ? { xs: 16, sm: 16, md: 24, lg: 24 }
            : { xs: 16, sm: 16, md: 24, lg: 16 },
          width: compact
            ? { xs: 40, sm: 40, md: 48, lg: 48 }
            : { xs: 40, sm: 40, md: 48, lg: 75 },
          height: compact
            ? { xs: 40, sm: 40, md: 48, lg: 48 }
            : { xs: 40, sm: 40, md: 48, lg: 72 },
          '& svg': { width: '100%', height: '100%' },
        }}
      />

      {/* Desktop (LG) — Figma: icon top-right, avatar, greeting, name, date */}
      <Box
        aria-hidden={!isLg}
        sx={{
          display: compact
            ? 'none'
            : { xs: 'none', sm: 'none', md: 'none', lg: 'flex' },
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <Avatar
          src={user?.avatar}
          altText={avatarAltText}
          fallbackText={initials || '?'}
          variant="circular"
          tabIndex={0}
          sx={{
            mt: LG_AVATAR_TOP,
            width: 40,
            height: 40,
            flexShrink: 0,
            backgroundColor: AVATAR_BG,
            color: AVATAR_TEXT_COLOR,
            fontSize: '1.25rem',
            fontWeight: 400,
            marginTop: { lg: '!important 111px' },
          }}
        />
        <Text
          component="h2"
          tabIndex={0}
          id={isLg ? 'home-greeting-heading' : undefined}
          sx={{
            m: 0,
            mt: LG_GREETING_GAP,
            p: 0,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            overflow: 'visible',
            fontWeight: lgGreetingTextSx.fontWeight,
            fontSize: lgGreetingTextSx.fontSize,
            lineHeight: lgGreetingTextSx.lineHeight,
            letterSpacing: lgGreetingTextSx.letterSpacing,
            fontFamily: lgGreetingTextSx.fontFamily,
            color: palette.common.white,
          }}
        >
          <Box component="span" sx={{ display: 'block' }}>
            {greetingLabel}{' '}
            <Box component="span" role="img" aria-label="waving hand">
              👋
            </Box>
          </Box>
          <Box
            component="span"
            sx={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              letterSpacing: '0.15px',
            }}
          >
            {firstName}
          </Box>
        </Text>

        <Box sx={{ flex: 1, minHeight: '16px' }} />

        <Text
          component="time"
          sx={{ m: 0, p: 0, flexShrink: 0, display: 'block', ...lgDateTextSx }}
          tabIndex={0}
        >
          {todayLabel}
        </Text>
      </Box>

      {/* Mobile + Tablet */}
      <Box
        aria-hidden={isLg}
        sx={{
          display: compact
            ? 'flex'
            : { xs: 'flex', sm: 'flex', md: 'flex', lg: 'none' },
          alignItems: 'center',
          height: '100%',
          gap: 2,
          pr: '56px',
          overflow: 'hidden',
          borderRadius: '8px',
        }}
        tabIndex={0}
      >
        <Avatar
          src={user?.avatar}
          altText={avatarAltText}
          fallbackText={initials || '?'}
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            backgroundColor: AVATAR_BG,
            color: AVATAR_TEXT_COLOR,
            fontSize: '0.875rem',
            fontWeight: 600,
          }}
          tabIndex={0}
        />

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Text
            component="h2"
            id={isLg ? undefined : 'home-greeting-heading'}
            sx={{
              m: 0,
              fontWeight: mobileGreetingTextSx.fontWeight,
              fontSize: mobileGreetingTextSx.fontSize,
              lineHeight: mobileGreetingTextSx.lineHeight,
              letterSpacing: mobileGreetingTextSx.letterSpacing,
              fontFamily: mobileGreetingTextSx.fontFamily,
              color: mobileGreetingTextSx.color,
              marginTop: mobileGreetingTextSx.marginTop,
            }}
            tabIndex={0}
          >
            {greetingLabel}
            <Box
              component="span"
              role="img"
              aria-label="waving hand"
              tabIndex={0}
            >
              {' '}
              👋
            </Box>
            , {firstName}
          </Text>

          <Text
            component="time"
            sx={{
              m: 0,
              mt: '2px',
              fontFamily: HOME_WIDGET_FONT_FAMILY,
              fontSize: '0.875rem',
              fontWeight: 400,
              lineHeight: '21px',
              color: GREETING_SUBTITLE_COLOR,
              letterSpacing: { lg: 0 },
              display: 'block',
            }}
            tabIndex={0}
          >
            {todayLabel}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};

export default HomeGreeting;
